import React, { useState, useRef, useMemo, useCallback } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import ExpenseModal from "../components/modal/ExpenseModal";
import API from "../api";
import toastr from "toastr";

export interface Expense {
  id?: number;
  description: string;
  payment_method: string;
  reference_no: string;
  amount: number;
  collected_date?: string;
}

const Expenses: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [totalAmount, setTotalAmount] = useState<number>(0); // ← NEW
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  });
  const tableRef = useRef<any>(null);

  const fetchExpenses = useCallback(
    async (
      page: number,
      search: string,
      sortBy = "entry_date",
      sortOrder = "desc"
    ) => {
      try {
        const res = await API.get(
          `/finance/getCustomerPayments?page=${page}&pageSize=25&search=${encodeURIComponent(
            search
          )}&sortBy=${sortBy}&sortOrder=${sortOrder}&startDate=${startDate}&endDate=${endDate}`
        );
        const data = res.data.data || [];
        // ── Calculate total from ALL returned rows ──
        const sum = data.reduce(
          (acc: number, row: Expense) => acc + Number(row.amount || 0),
          0
        );
        setTotalAmount(sum); // ← NEW
        return { data, total: res.data.total };
      } catch {
        toastr.error("Failed to fetch expenses");
        setTotalAmount(0);
        return { data: [], total: 0 };
      }
    },
    [startDate, endDate]
  );

  const handleDateFilter = (e: React.FormEvent) => {
    e.preventDefault();
    if (new Date(endDate) < new Date(startDate)) {
      toastr.warning("End date should be greater than Start date");
      return;
    }
    tableRef.current?.refetch();
  };

  const handleAdd = () => {
    setModalMode("add");
    setSelectedExpense(null);
    setShowModal(true);
  };

  const handleEdit = (expense: Expense) => {
    setModalMode("edit");
    setSelectedExpense(expense);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;
    try {
      await API.delete(`/finance/deleteCustomerPayment/${id}`);
      toastr.success("Expense deleted successfully");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete expense");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedExpense(null);
  };

  const columns = useMemo(
    () => [
      { header: "#", accessorKey: "id" },
      {
        header: "Date",
        accessorKey: "collected_date",
        cell: ({ row }: any) => {
          const date = row.original.collected_date;
          return date ? new Date(date).toLocaleDateString("en-GB") : "-";
        },
      },
      { header: "Description", accessorKey: "description" },
      { header: "Payment Method", accessorKey: "payment_method" },
      { header: "Reference No.", accessorKey: "reference_no" },
      {
        header: "Amount",
        accessorKey: "amount",
        cell: ({ row }: any) =>
          `Rs. ${Number(row.original.amount).toFixed(2)}`,
      },
      {
        header: "Action",
        id: "actions",
        cell: ({ row }: any) => {
          const data: Expense = row.original;
          return (
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-light"
                onClick={() => handleEdit(data)}
                title="Edit"
              >
                <i className="fa fa-edit" />
              </button>
              <button
                className="btn btn-light"
                onClick={() => handleDelete(data.id!)}
                title="Delete"
              >
                <i className="fa fa-trash" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <ContentHeader title="Expense" />
        <button className="btn btn-light px-5" onClick={handleAdd}>
          <i className="fa fa-plus" /> Create New
        </button>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-header">
              <form onSubmit={handleDateFilter} className="d-flex gap-2">
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <button type="submit" className="btn btn-light" title="Search">
                  <em className="fa fa-search" />
                </button>
              </form>
            </div>

            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchExpenses}
                queryKey={["expenses", startDate, endDate]}
              />

              {/* ── TOTAL ROW (matches image exactly) ── */}
              <table
                className="table table-bordered"
                style={{ marginTop: "-1px" }}
              >
                <tfoot>
                  <tr>
                    <td
                      colSpan={5}
                      className="text-end fw-bold"
                      style={{ color: "#4fc3f7" }}
                    >
                      Total
                    </td>
                    <td className="fw-bold" style={{ color: "#4fc3f7" }}>
                      Rs. {totalAmount.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <ExpenseModal
          show={showModal}
          onClose={handleModalClose}
          mode={modalMode}
          expenseData={selectedExpense}
          onSuccess={() => {
            handleModalClose();
            tableRef.current?.refetch();
          }}
        />
      )}
    </div>
  );
};

export default Expenses;